import React from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"
import * as serviceWorker from "./serviceWorker"
import { installStaleBuildRecovery } from "./utils/staleBuild"
import { announceUpdate } from "./utils/serviceWorkerUpdates"
import { answerCapabilityQueries } from "./utils/serviceWorkerCapability"
import request from "./utils/request"
import Store from "./store"
import App from "./components"

const AppWrap = () => (
	<Provider store={Store}>
		<App />
	</Provider>
)

// Before anything renders: a deploy can leave this page holding a chunk
// manifest the server no longer has, and with no error boundary that blanks the
// whole app. See utils/staleBuild.js.
//
// Production only, because the cause is production only -- serviceWorker.js:24
// registers under the same condition. In development a failed chunk is usually
// the dev server restarting, and silently reloading mid-debug would hide it.
if (process.env.NODE_ENV === "production") installStaleBuildRecovery()

// Also before anything renders, and deliberately not gated on NODE_ENV: a newly
// installed worker asks the open windows whether they can offer the update
// themselves, and a window that does not answer is reloaded onto the new build
// because that is the only way to reach a bundle with no banner in it. So this
// listener has to exist by the time the question is asked, which can be during
// the very load that is registering it, and being one line earlier than the
// render is the cheapest insurance available. Without a worker it is inert.
// See utils/serviceWorkerCapability.js and the forced-update section of
// sw-custom.js.
answerCapabilityQueries()

window.addEventListener("online", request.resumeRevealAnswers)
window.addEventListener("storage", event => {
	if (event.key === "account" || event.key === null) request.handleAccountStorageChange(event.newValue)
})
document.addEventListener("visibilitychange", () => {
	if (document.visibilityState === "visible") request.resumeRevealAnswers()
})

const container = document.getElementById("root")
const root = createRoot(container)
root.render(<AppWrap />)

// Enable service worker for offline support and faster loading
// Assets will be cached locally after first load
//
// onUpdate fires once a newly deployed worker has claimed this page, which
// UpdateAvailableBanner turns into a "New version ready" prompt. The
// announcement is latched in utils/serviceWorkerUpdates.js, so firing before
// React has mounted -- which it usually does -- is not a missed deploy.
serviceWorker.register({ onUpdate: announceUpdate })
